import { DefaultConnectionIo } from "panda-configurator-shared";
import {
	RawLogicsConnection,
	RawLogicsInput,
	RawLogicsOperator,
	RawLogicsOutput,
} from "../rawLogics/RawOperator";
import { LogicsData, LogicsVariable, fromRawVariables } from "./LogicsVariable";

export interface LogicsOperator {
	name: string;
	description: string;

	hexId: string;

	inputs: LogicsIo[];
	outputs: LogicsIo[];

	data?: LogicsData[];
	variables: LogicsVariable[];

	size: number;

	tags: string[];
}

export interface LogicsIo {
	label: string;
	description: string;
	type: string;
	required: boolean;
	enabledByDefault: boolean;
	size: number;
	connection: LogicsConnection;
	defaultConnection: DefaultConnectionIo | null;
	allowedMemories?: string[];
}

type LogicsConnection =
	| LogicsConnectionModeSingle
	| LogicsConnectionModeMultiple
	| LogicsConnectionModeSpread;

interface LogicsConnectionModeSingle {
	mode: "single";
}

interface LogicsConnectionModeMultiple {
	mode: "multiple";
	counter: string;
}

interface LogicsConnectionModeSpread {
	mode: "spread";
	counter: string;
}

export function fromRawOperators(
	rawOperators: RawLogicsOperator[],
): LogicsOperator[] {
	return rawOperators.map(fromRawOperator);
}

function fromRawOperator(rawOperator: RawLogicsOperator): LogicsOperator {
	return {
		name: rawOperator.name,
		description: rawOperator.description,

		hexId: rawOperator.hexId ?? "",

		// TODO: maybe add data to operators
		data: undefined, //rawOperator.data?.map(fromRawData),
		variables: fromRawVariables(rawOperator.variables),

		tags: rawOperator.tags ?? [],

		size: rawOperator?.size ?? 24,

		inputs: rawOperator.inputs.map(fromRawInput),
		outputs: rawOperator.outputs.map(fromRawOutput),
	};
}

function fromRawInput(rawIo: RawLogicsInput): LogicsIo {
	return {
		label: rawIo.name,
		description: rawIo.description ?? "[ No description ]",
		type: rawIo.type,
		required: (rawIo.required ?? false) || rawIo.connection_mode === "spread",
		enabledByDefault: rawIo.enabled_by_default ?? true,
		size: rawIo.type == "bool" ? 1 : Number(rawIo.type.replace(/\D/g, "")) / 8,
		connection: connectionFromRawIo(rawIo),
		defaultConnection: rawIo.default_connection ?? null,
		allowedMemories: rawIo.allowed_memories ?? [],
	};
}

function fromRawOutput(rawIo: RawLogicsOutput): LogicsIo {
	return {
		label: rawIo.name,
		description: rawIo.description ?? "[ No description ]",
		type: rawIo.type,
		required: (rawIo.required ?? false) || rawIo.connection_mode === "spread",
		enabledByDefault: rawIo.enabled_by_default ?? true,
		size: rawIo.type == "bool" ? 1 : Number(rawIo.type.replace(/\D/g, "")) / 8,
		connection: connectionFromRawIo(rawIo),
		defaultConnection: rawIo.default_connection ?? null,
	};
}

function connectionFromRawIo(
	connection: RawLogicsConnection,
): LogicsConnection {
	switch (connection.connection_mode) {
		case "single":
			return {
				mode: "single",
			};
		case "multiple":
			if (connection.connection_counter === undefined) {
				throw new Error(`Multiple connection has no counter`);
			}

			return {
				mode: "multiple",
				counter: connection.connection_counter,
			};
		case "spread":
			if (connection.connection_counter === undefined) {
				throw new Error(`Spread connection has no counter`);
			}

			return {
				mode: "spread",
				counter: connection.connection_counter,
			};
		default:
			let _: never = connection;
			throw new Error(`Corrupted connection mode`);
	}
}
